import { RULES, replay } from "./rules.js";
import type { Game } from "./store.js";

/**
 * Statistics over finished games (PRD FR-6, FR-7).
 *
 * Everything is derived from the stored turn logs on demand -- nothing is kept
 * as a running total, so deleting a game (FR-5.3) simply removes it from every
 * figure with no separate bookkeeping to get out of step.
 */

export interface PlayerStats {
  playerId: string;
  games: number;
  wins: number;
  /** Percentage, 0-100. */
  winRate: number;
  points: number;
  /** Points banked before penalties, for the banking average. */
  bankedPoints: number;
  turns: number;
  /** Average per banked turn; a struck turn is not a small score, it is none. */
  average: number;
  best: number;
  strikes: number;
  /** Percentage of turns that were strikes. */
  strikeRate: number;
  penalties: number;
}

function blank(playerId: string): PlayerStats {
  return {
    playerId,
    games: 0,
    wins: 0,
    winRate: 0,
    points: 0,
    bankedPoints: 0,
    turns: 0,
    average: 0,
    best: 0,
    strikes: 0,
    strikeRate: 0,
    penalties: 0,
  };
}

/**
 * Aggregate the given games. FR-1.6: a player with no games must come out with
 * zeroes rather than a division by zero, so every ratio is guarded.
 */
export function statsFor(games: Game[], playerIds: string[] = []): PlayerStats[] {
  const acc = new Map<string, PlayerStats>();
  const ensure = (id: string): PlayerStats => {
    let row = acc.get(id);
    if (!row) {
      row = blank(id);
      acc.set(id, row);
    }
    return row;
  };
  // Seed the roster so players who never played still appear, with zeroes.
  for (const id of playerIds) ensure(id);

  for (const game of games) {
    const state = replay(game.order, game.turns);
    const winner = state.standings[0];
    for (const player of state.players) {
      const row = ensure(player.playerId);
      row.games += 1;
      row.points += player.total;
      row.bankedPoints += player.bankedPoints;
      row.turns += player.turns;
      row.strikes += player.struck;
      row.penalties += player.penalties;
      row.best = Math.max(row.best, player.best);
      if (winner && winner.playerId === player.playerId) row.wins += 1;
    }
  }

  const out = [...acc.values()];
  for (const row of out) {
    row.winRate = row.games > 0 ? Math.round((row.wins / row.games) * 100) : 0;
    const banked = row.turns - row.strikes;
    // Average of banked points over banked turns -- penalties belong to neither.
    row.average = banked > 0 ? Math.round(row.bankedPoints / banked) : 0;
    row.strikeRate = row.turns > 0 ? Math.round((row.strikes / row.turns) * 100) : 0;
  }

  // Most wins first, then the better average -- someone with one lucky win
  // should not outrank a steady player with the same count.
  out.sort((a, b) => b.wins - a.wins || b.average - a.average || b.points - a.points);
  return out;
}

export interface FunFact {
  icon: string;
  label: string;
  value: string;
  sub: string;
}

/**
 * FR-6.2. Facts with no data are left out rather than shown as zero: "longest
 * game: 0 turns" is noise, not a fact.
 */
export function funFacts(games: Game[], names: Record<string, string>): FunFact[] {
  if (games.length === 0) return [];
  const nameOf = (id: string): string => names[id] ?? id;
  const facts: FunFact[] = [];

  let biggest = { points: 0, who: "", when: 0 };
  let longest = { turns: 0, when: 0 };
  let shortest = { turns: Number.POSITIVE_INFINITY, when: 0 };
  let strikes = 0;
  let penalties = 0;
  let totalTurns = 0;
  let record = { total: 0, who: "", when: 0 };
  let closest = { margin: Number.POSITIVE_INFINITY, who: "", when: 0 };
  let streak = { length: 0, who: "" };
  const wins = new Map<string, number>();

  for (const game of games) {
    for (const turn of game.turns) {
      totalTurns += 1;
      if (turn.type === "strike") strikes += 1;
      if (turn.type === "bank" && turn.points > biggest.points) {
        biggest = { points: turn.points, who: nameOf(turn.playerId), when: game.startedAt };
      }
    }
    if (game.turns.length > longest.turns) longest = { turns: game.turns.length, when: game.startedAt };
    if (game.turns.length > 0 && game.turns.length < shortest.turns) {
      shortest = { turns: game.turns.length, when: game.startedAt };
    }

    const state = replay(game.order, game.turns);
    for (const player of state.players) {
      penalties += player.penalties;
      if (player.bestStreak > streak.length) {
        streak = { length: player.bestStreak, who: nameOf(player.playerId) };
      }
    }

    // The standings are only a settled result once the game is over; an
    // in-progress game has no winner and no final margin.
    const [first, second] = state.standings;
    if (state.finished && first) {
      wins.set(first.playerId, (wins.get(first.playerId) ?? 0) + 1);
      if (first.total > record.total) {
        record = { total: first.total, who: nameOf(first.playerId), when: game.startedAt };
      }
      if (second) {
        const margin = first.total - second.total;
        if (margin < closest.margin) {
          closest = { margin, who: nameOf(first.playerId), when: game.startedAt };
        }
      }
    }
  }

  const champion = [...wins.entries()].sort((a, b) => b[1] - a[1])[0];

  const date = (ms: number): string =>
    new Date(ms).toLocaleDateString("de-DE", { day: "numeric", month: "long" });

  facts.push({
    icon: "🎲",
    label: "Spiele gespielt",
    value: String(games.length),
    sub: `${totalTurns} Züge insgesamt`,
  });

  if (biggest.points > 0) {
    facts.push({
      icon: "🚀",
      label: "Größter Wurf",
      value: biggest.points.toLocaleString("de-DE"),
      sub: `${biggest.who}, ${date(biggest.when)}`,
    });
  }
  if (longest.turns > 0) {
    facts.push({
      icon: "⏳",
      label: "Längstes Spiel",
      value: `${longest.turns} Züge`,
      sub: date(longest.when),
    });
  }
  if (Number.isFinite(shortest.turns) && shortest.turns !== longest.turns) {
    facts.push({
      icon: "⚡",
      label: "Kürzestes Spiel",
      value: `${shortest.turns} Züge`,
      sub: date(shortest.when),
    });
  }
  if (strikes > 0) {
    facts.push({
      icon: "✏️",
      label: "Striche",
      value: String(strikes),
      sub: `${Math.round((strikes / totalTurns) * 100)} % aller Züge`,
    });
  }
  if (penalties > 0) {
    facts.push({
      icon: "💸",
      label: "Strafen",
      value: String(penalties),
      sub: `${(penalties * RULES.penalty).toLocaleString("de-DE")} Punkte verloren`,
    });
  }
  if (record.total > 0) {
    facts.push({
      icon: "🏆",
      label: "Rekordsieg",
      value: record.total.toLocaleString("de-DE"),
      sub: `${record.who}, ${date(record.when)}`,
    });
  }
  if (Number.isFinite(closest.margin)) {
    facts.push({
      icon: "🤏",
      label: "Knappster Sieg",
      value: `${closest.margin.toLocaleString("de-DE")} Punkte`,
      sub: `${closest.who}, ${date(closest.when)}`,
    });
  }
  if (streak.length >= 3) {
    facts.push({
      icon: "🔥",
      label: "Längste Serie",
      value: `${streak.length}×`,
      sub: `${streak.who} ohne Strich`,
    });
  }
  // Only worth calling out once somebody is actually ahead on wins.
  if (champion && champion[1] >= 2) {
    facts.push({
      icon: "👑",
      label: "Seriensieger",
      value: `${champion[1]} Siege`,
      sub: nameOf(champion[0]),
    });
  }

  return facts;
}

/**
 * Rank title from lifetime wins (FR-7.3). The thresholds are the PRD's, and
 * they are inclusive-lower: exactly 3 wins is already Aufsteiger.
 */
export function rankTitle(wins: number): string {
  if (wins >= 15) return "Legende";
  if (wins >= 10) return "Serientäter";
  if (wins >= 6) return "Stammsieger";
  if (wins >= 3) return "Aufsteiger";
  if (wins >= 1) return "Sieger";
  return "Neuling";
}
